/**
 * Test mode — lets the owner self-exclude their own testing from analytics.
 *
 * Activate by visiting any page with `?test=<token>` (the secret
 * WL_TEST_MODE_TOKEN). The token is stored in localStorage and sent on every
 * API request as the `X-WL-Test-Token` header; the backend then flags that
 * session's Player as is_test=True, so the dashboards exclude all its activity.
 *
 * Deactivate with `?test=off` (or by tapping the on-screen badge). In an
 * incognito window localStorage is per-window, so re-activate with the `?test=`
 * URL each fresh window — which is exactly the testing workflow.
 */

const KEY = 'wl_test_token';
const OFF_VALUES = new Set(['off', '0', 'false', 'no', '']);

function _safeLocal(fn, fallback) {
    try {
        return fn();
    } catch (e) {
        return fallback;
    }
}

/** Current token, or '' if test mode is not active. Read fresh each call so
 *  disabling takes effect immediately. */
export function getTestToken() {
    return _safeLocal(() => localStorage.getItem(KEY) || '', '');
}

function clearTestMode() {
    _safeLocal(() => localStorage.removeItem(KEY));
    const badge = document.getElementById('wl-test-badge');
    if (badge) badge.remove();
}

function renderBadge() {
    if (!getTestToken()) return;
    if (typeof document === 'undefined' || !document.body) return;
    if (document.getElementById('wl-test-badge')) return;

    const badge = document.createElement('div');
    badge.id = 'wl-test-badge';
    badge.setAttribute('role', 'status');
    badge.textContent = '🧪 TEST MODE: excluded from analytics (tap to exit)';
    badge.style.cssText = [
        'position:fixed', 'left:50%', 'bottom:12px', 'transform:translateX(-50%)',
        'z-index:2147483647', 'background:#b00020', 'color:#fff',
        'font:600 12px/1.2 system-ui,-apple-system,sans-serif',
        'padding:7px 12px', 'border-radius:999px', 'cursor:pointer',
        'box-shadow:0 2px 8px rgba(0,0,0,.35)', 'opacity:.92',
        'user-select:none', '-webkit-user-select:none', 'pointer-events:auto',
    ].join(';');
    badge.addEventListener('click', clearTestMode);
    document.body.appendChild(badge);
}

/** Capture `?test=` from the URL (once per load), persist it, and show the
 *  badge if active. Call early at app startup. */
export function initTestMode() {
    _safeLocal(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('test')) {
            const val = (params.get('test') || '').trim();
            if (OFF_VALUES.has(val.toLowerCase())) {
                localStorage.removeItem(KEY);
            } else {
                localStorage.setItem(KEY, val);
            }
        }
    });

    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', renderBadge, { once: true });
        } else {
            renderBadge();
        }
    }
}
